/**
 * Ticket SK2: Bestätigungsseite nach der Zahlung.
 *
 * Diese Seite liest nur. Sie darf keinen Zustand setzen — allein die geprüfte
 * Rückmeldung des Zahlungsdienstes entscheidet, ob ein Kauf bezahlt ist. Wer
 * die Adresse von Hand aufruft, erzeugt damit nichts.
 *
 * Solange die Bestätigung noch unterwegs ist, fragt die Seite in Abständen
 * erneut nach, ohne den Besucher zum Aktualisieren zu drängen.
 */

import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { Layout } from "@/components/Layout";
import { getPurchaseState } from "@/lib/purchases.functions";
import { PURCHASE_STATE_TEXTS } from "@/lib/purchases.shared";
import { markSent, pushEcommerceEvent, wasSentBefore } from "@/lib/datalayer";

const TITLE = "Zahlung bestätigt | creategy";
const DESCRIPTION = "Rückmeldung zu Ihrem Kauf bei creategy.";

export const Route = createFileRoute("/kaufen/bestaetigung")({
  validateSearch: z.object({ vorgang: z.string().trim().max(200).optional() }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ConfirmationPage,
});

function ConfirmationPage() {
  const { vorgang } = Route.useSearch();
  const load = useServerFn(getPurchaseState);

  const query = useQuery({
    queryKey: ["purchase-state", vorgang],
    queryFn: () => load({ data: { sessionId: vorgang! } }),
    enabled: Boolean(vorgang),
    retry: false,
    // Nachfragen, solange die Rückmeldung noch unterwegs sein kann.
    refetchInterval: (q) => (q.state.data?.view === "wartet" ? 4000 : false),
  });

  // Kaufereignis genau einmal je Vorgang, auch über Neuladen hinweg. Die Werte
  // liefert der Server erst, wenn der Kauf in purchases „bezahlt“ ist.
  const purchase = query.data?.purchase ?? null;
  useEffect(() => {
    if (!purchase) return;
    const key = `creategy_purchase_sent:${purchase.transactionId}`;
    if (wasSentBefore(key)) return;
    markSent(key);
    const value = purchase.valueCents / 100;
    pushEcommerceEvent({
      event: "purchase",
      ecommerce: {
        transaction_id: purchase.transactionId,
        currency: "EUR",
        value,
        items: [
          { item_id: purchase.itemId, item_name: purchase.itemName, price: value, quantity: 1 },
        ],
      },
    });
  }, [purchase]);

  const view = query.data?.view ?? "wartet";
  const texts = PURCHASE_STATE_TEXTS[view];

  return (
    <Layout>
      <section>
        <div className="section-y mx-auto w-full max-w-[820px] px-6 md:px-12">
          <h1 className="heading-h1">{vorgang ? texts.title : "Kein Vorgang angegeben"}</h1>
          <p className="mt-6 font-body text-base text-muted-foreground">
            {vorgang
              ? texts.body
              : "Diese Seite zeigt die Rückmeldung zu einem Kauf. Ohne Vorgang gibt es nichts anzuzeigen."}
          </p>

          {query.data?.serviceTitle && (
            <div className="mt-8 rounded-sm border border-foreground/15 p-6">
              <p className="font-display text-base font-extrabold text-primary">
                {query.data.serviceTitle}
              </p>
              {query.data.amountCents !== null && (
                <p className="mt-2 font-body text-sm text-muted-foreground">
                  {new Intl.NumberFormat("de-DE", {
                    style: "currency",
                    currency: "EUR",
                  }).format(query.data.amountCents / 100)}
                </p>
              )}
            </div>
          )}

          <p className="mt-10 font-body text-sm text-muted-foreground">
            Fragen zum Kauf? Schreiben Sie an{" "}
            <a className="underline" href="mailto:hello@creategy.de">
              hello@creategy.de
            </a>
            .
          </p>
        </div>
      </section>
    </Layout>
  );
}
